import { useMemo, useState, type FC } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { dayjs } from "@thalamos/common";

import { dashboardTileColour } from "./dashboardTileColour.js";
import { FormTitle, LoadingSpinner } from "~/components/design-system/index.js";

export type DashboardChooserItem = {
  key: string;
  label: string;
  lastViewedAt?: string | null;
  tileImage?: string;
  onSelect: () => void;
};

export type DashboardChooserProps =
  | { type: "loading" }
  | { type: "loaded"; items: DashboardChooserItem[] };

// Stable reference so the `useMemo` below is not invalidated every render.
const EMPTY_ITEMS: DashboardChooserItem[] = [];

export const DashboardChooser: FC<DashboardChooserProps> = (props) => {
  const theme = useTheme();
  const [query, setQuery] = useState("");

  const items = props.type === "loaded" ? props.items : EMPTY_ITEMS;
  const trimmedQuery = query.trim().toLowerCase();
  const filteredItems = useMemo(
    () =>
      trimmedQuery
        ? items.filter((item) =>
            item.label.toLowerCase().includes(trimmedQuery),
          )
        : items,
    [items, trimmedQuery],
  );

  return (
    <Box sx={{ p: theme.spacing(4, 4, 4, 2) }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <FormTitle
          titleText="Dashboard Overview"
          hasTitleBottomMargin={false}
          hasContainerMarginBottom={false}
          useReducedTopPadding={true}
        />
        {props.type === "loaded" && props.items.length > 0 && (
          <TextField
            placeholder="Search for dashboard"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            sx={{ width: 360, maxWidth: "50%" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
              htmlInput: { "aria-label": "Search for dashboard" },
            }}
          />
        )}
      </Box>

      <Box sx={{ mt: 4 }}>
        {props.type === "loading" ? (
          <LoadingSpinner label="Loading..." />
        ) : props.items.length === 0 ? (
          <Typography>No dashboards are configured for your team.</Typography>
        ) : filteredItems.length === 0 ? (
          <Typography>No dashboards match your search.</Typography>
        ) : (
          <Grid container spacing={3}>
            {filteredItems.map((item) => (
              <Grid key={item.key} size={{ xs: 12, sm: 6 }}>
                <Card>
                  <CardActionArea
                    onClick={() => {
                      item.onSelect();
                    }}
                  >
                    {/* Tile image if the dashboard has one, otherwise a stable
                        per-dashboard colour. */}
                    {item.tileImage ? (
                      <Box
                        component="img"
                        src={item.tileImage}
                        alt=""
                        sx={{
                          height: 140,
                          width: "100%",
                          objectFit: "cover",
                          display: "block",
                          backgroundColor: "#f5f5f5",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 140,
                          backgroundColor: dashboardTileColour(item.key, theme),
                        }}
                      />
                    )}
                    <CardContent
                      sx={{ backgroundColor: theme.palette.common.midBlue }}
                    >
                      <Typography sx={{ fontWeight: 700 }} noWrap>
                        {item.label}
                      </Typography>
                      {/* minHeight reserves the caption line so cards are the
                          same height whether or not a last-viewed date shows. */}
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.secondary",
                          minHeight: "1.25rem",
                        }}
                      >
                        {item.lastViewedAt
                          ? `Last viewed ${dayjs(item.lastViewedAt).format("D MMMM YYYY")}`
                          : ""}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};
